import { ref, type Ref } from 'vue'

export interface DialogState {
  isOpen: Ref<boolean>
  open: () => void
  close: () => void
  toggle: () => void
}

export interface DialogOptions {
  defaultOpen?: boolean
  onOpen?: () => void
  onClose?: () => void
}

/**
 * Composable for managing dialog state
 * Provides centralized dialog management with callbacks
 */
export function useDialog(options: DialogOptions = {}): DialogState {
  const { defaultOpen = false, onOpen, onClose } = options

  const isOpen = ref(defaultOpen)

  const open = () => {
    isOpen.value = true
    onOpen?.()
  }

  const close = () => {
    isOpen.value = false
    onClose?.()
  }

  const toggle = () => {
    if (isOpen.value) {
      close()
    } else {
      open()
    }
  }

  return {
    isOpen,
    open,
    close,
    toggle
  }
}

/**
 * Composable for managing multiple dialogs in a component
 * Useful when a component needs multiple dialogs
 */
export function useDialogs<T extends Record<string, DialogOptions>>(dialogs: T) {
  const dialogStates = {} as {
    [K in keyof T]: DialogState
  }

  for (const [key, options] of Object.entries(dialogs) as [keyof T, DialogOptions][]) {
    dialogStates[key] = useDialog(options)
  }

  return dialogStates
}